import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import Snackbar from "@mui/material/Snackbar";
import MuiAlert from "@mui/material/Alert";
import Tooltip from "@mui/material/Tooltip";
import { IoMdArrowBack } from "react-icons/io";
import API_URL from "../../src/Config";
import Select from "react-select";
import { parseMiApiError } from "./miIndexUtils";

function EditLocation({ isSidebarExpanded }) {
  const navigate = useNavigate();
  const { id } = useParams();
  const [errors, setErrors] = useState({});
  const [severity, setSeverity] = useState("success");
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const [locationData, setLocationData] = useState({
    location_name: "",
    location_type: "",
    region: "",
    state_office: "",
    zone: "",
    tankage_capacity: "",
  });

  const locationTypeOptions = ["Depot", "Terminal", "AFS"];
  const zoneOptions = ["A", "B", "C"];

  const locationTypeOptionsFormatted = locationTypeOptions.map((type) => ({
    value: type,
    label: type,
  }));

  const zoneOptionsFormatted = zoneOptions.map((zone) => ({
    value: zone,
    label: zone,
  }));

  const handleKeyDown = (event) => {
    if (event.key === "Enter") {
      event.preventDefault();
      handleSaveClick();
    }
  };

  useEffect(() => {
    const fetchLocationData = async () => {
      try {
        setIsLoading(true);
        const authKey = localStorage.getItem("authKey");
        const response = await axios.get(`${API_URL}/api/mi/locations/${id}`, {
          headers: { Authorization: authKey },
        });

        const responseData = response.data;
        if (!responseData) throw new Error("Invalid API response");

        setLocationData({
          location_name: responseData.location_name || "",
          location_type: responseData.location_type || "",
          region: responseData.region || "",
          state_office: responseData.state_office || "",
          zone: responseData.zone || "",
          tankage_capacity: responseData.tankage_capacity || "",
        });

        setIsLoading(false);
      } catch (error) {
        console.error("Error fetching location data:", error.message);
        setSeverity("error");
        showSnackbar(
          parseMiApiError(error, "An error occurred while fetching location data"),
        );
        setIsLoading(false);
      }
    };

    fetchLocationData();
  }, [id]);

  const validateForm = () => {
    const validationErrors = {};

    if (!locationData.location_name.trim()) {
      validationErrors.location_name = "Location name is required.";
    }

    if (!locationData.location_type) {
      validationErrors.location_type = "Location type is required.";
    }

    setErrors(validationErrors);
    return Object.keys(validationErrors).length === 0;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setLocationData({ ...locationData, [name]: value });
    setErrors((prevState) => ({
      ...prevState,
      [name]: "",
    }));
  };

  const handleCancelClick = () => {
    window.history.back();
  };

  const handleSnackbarClose = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }
    setSnackbarOpen(false);
  };

  const showSnackbar = (message) => {
    setSnackbarMessage(message);
    setSnackbarOpen(true);
  };

  const handleSaveClick = async () => {
    if (!validateForm()) {
      return;
    }

    try {
      setIsLoading(true);
      const authKey = localStorage.getItem("authKey");

      const payload = {
        location_name: locationData.location_name.trim(),
        location_type: locationData.location_type,
        region: locationData.region.trim() || null,
        state_office: locationData.state_office.trim() || null,
        zone: locationData.zone || null,
        tankage_capacity: locationData.tankage_capacity
          ? parseFloat(locationData.tankage_capacity)
          : null,
      };

      const response = await axios.put(
        `${API_URL}/api/mi/locations/${id}`,
        payload,
        {
          headers: {
            Authorization: authKey,
          },
        }
      );

      if (response.status === 200) {
        setSeverity("success");
        showSnackbar(response.data?.message || "Location updated successfully");
        setTimeout(() => {
          navigate("/mi-locations");
        }, 1000);
      }
    } catch (error) {
      console.error("Error updating location:", error.message);
      setSeverity("error");
      const errorMessage = parseMiApiError(
        error,
        "An error occurred while updating location",
      );
      showSnackbar(errorMessage);
      setIsLoading(false);
    }
  };

  const handleBackClick = () => {
    window.history.back();
  };

  return (
    <>
      <div
        className={`data-edit-form-content-container page-content ${
          isSidebarExpanded ? "expanded" : "collapsed"
        }`}
      >
        <div className="data-edit-form-content">
          <div className="data-edit-form-header">
            <div className="data-edit-form-header-content">
              <div className="data-edit-form-heading-content-left-side">
                <Tooltip title="Back" arrow>
                  <IoMdArrowBack
                    className="back-company"
                    onClick={handleBackClick}
                  />
                </Tooltip>
                Edit Location
              </div>
              <div className="data-edit-form-heading-content-right-side"></div>
            </div>
          </div>
          <div className="header-separator"></div>
          <div className="data-edit-form-body">
            <form onKeyDown={handleKeyDown}>
              <div className="new-data-form">
                <div className="form-group">
                  <label htmlFor="location_name">
                    Location Name<span className="text-danger">*</span>
                  </label>
                  <div className="input-group">
                    <input
                      type="text"
                      name="location_name"
                      value={locationData.location_name}
                      onChange={handleInputChange}
                      placeholder="Enter location name"
                    />
                  </div>
                  {errors.location_name && (
                    <div className="error-message">{errors.location_name}</div>
                  )}
                </div>

                <div className="form-group">
                  <label htmlFor="location_type">
                    Location Type<span className="text-danger">*</span>
                  </label>
                  <Select
                    name="location_type"
                    placeholder="Select Location Type"
                    options={locationTypeOptionsFormatted}
                    value={
                      locationTypeOptionsFormatted.find(
                        (opt) => opt.value === locationData.location_type
                      ) || null
                    }
                    onChange={(selected) =>
                      handleInputChange({
                        target: {
                          name: "location_type",
                          value: selected ? selected.value : "",
                        },
                      })
                    }
                    classNamePrefix="react-select"
                  />
                  {errors.location_type && (
                    <div className="error-message">{errors.location_type}</div>
                  )}
                </div>

                <div className="form-group">
                  <label htmlFor="region">Region</label>
                  <div className="input-group">
                    <input
                      type="text"
                      name="region"
                      value={locationData.region}
                      onChange={handleInputChange}
                      placeholder="Enter region"
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="state_office">State Office</label>
                  <div className="input-group">
                    <input
                      type="text"
                      name="state_office"
                      value={locationData.state_office}
                      onChange={handleInputChange}
                      placeholder="Enter state office"
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="zone">Zone</label>
                  <Select
                    name="zone"
                    placeholder="Select Zone"
                    options={zoneOptionsFormatted}
                    value={
                      zoneOptionsFormatted.find(
                        (opt) => opt.value === locationData.zone
                      ) || null
                    }
                    onChange={(selected) =>
                      handleInputChange({
                        target: {
                          name: "zone",
                          value: selected ? selected.value : "",
                        },
                      })
                    }
                    classNamePrefix="react-select"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="tankage_capacity">Tankage Capacity</label>
                  <div className="input-group">
                    <input
                      type="number"
                      step="0.01"
                      name="tankage_capacity"
                      value={locationData.tankage_capacity}
                      onChange={handleInputChange}
                      placeholder="Enter tankage capacity"
                    />
                  </div>
                </div>
              </div>
            </form>
          </div>
          <div className="footer">
            <div className="button-group">
              <button
                className="data-edit-form-save-button"
                type="button"
                onClick={handleSaveClick}
                disabled={isLoading}
              >
                {isLoading ? "Saving..." : "Save"}
              </button>
              <button
                className="data-edit-form-cancel-button"
                onClick={handleCancelClick}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      </div>
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        style={{ marginTop: "35px", marginLeft: "20px" }}
      >
        <MuiAlert onClose={handleSnackbarClose} severity={severity}>
          {snackbarMessage}
        </MuiAlert>
      </Snackbar>
    </>
  );
}

export default EditLocation;
